import { getFirstTwoInitials } from '@Sage/ssm-utils';
import ColorHash from 'color-hash';

import TagElement from '../TagElement';
const colorHash = new ColorHash();

let srcPlaceholders = {};
let srcHasPlaceholder = {};
let existingPlaceholders = {};

// Same path as @Sage/hoi-poi-ui Icon `lock` (rawIcons/Lock) for parity with detail ReadOnlyIndicator.
const LOCK_ICON_SVG =
    '<svg viewBox="0 0 11 14" width="14" height="14" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg">' +
    '<path fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" d="M9.333 4.667h-.666V3.333a3.335 3.335 0 00-6.667 0v1.334h-.667C.6 4.667 0 5.267 0 6v6.667C0 13.4.6 14 1.333 14h8c.734 0 1.334-.6 1.334-1.333V6c0-.733-.6-1.333-1.334-1.333zm-4 6C4.6 10.667 4 10.067 4 9.333 4 8.6 4.6 8 5.333 8c.734 0 1.334.6 1.334 1.333 0 .734-.6 1.334-1.334 1.334zm2.067-6H3.267V3.333c0-1.14.926-2.066 2.066-2.066 1.14 0 2.067.926 2.067 2.066v1.334z"/>' +
    '</svg>';

const isReadOnlyFlag = (value) => {
    if (typeof value === 'boolean') return value;
    return value === 'True' || value === 'true' || value === '1' || value === 1;
};

const getReadOnlyValue = (readOnlyIndicator, data) => {
    const configuredField = readOnlyIndicator?.field;
    const configuredValue = configuredField ? data?.[configuredField] : undefined;
    if (configuredValue !== undefined && configuredValue !== null) return configuredValue;

    return data?.isReadOnly ?? data?.readOnlyCompany ?? data?.ReadOnlyCompany ?? data?.readOnly;
};

const createReadOnlyIndicator = (readOnlyIndicator, data) => {
    if (!readOnlyIndicator) return null;
    if (!isReadOnlyFlag(getReadOnlyValue(readOnlyIndicator, data))) return null;

    const indicator = document.createElement('span');
    indicator.classList.add('w5-avatar-cell__readonly-indicator');
    indicator.innerHTML = LOCK_ICON_SVG;
    if (readOnlyIndicator.tooltip) indicator.title = readOnlyIndicator.tooltip;
    return indicator;
};

export default function Avatar(params) {
    const {
        data,
        otherFields,
        isSquared,
        imageSize,
        value,
        innerMethods,
        reverse,
        onlyIcon,
        otherParams,
    } = params;
    const readOnlyIndicatorConfig =
        params.readOnlyIndicator ?? params.colDef?.cellRendererParams?.readOnlyIndicator;
    const readOnlyIndicator = createReadOnlyIndicator(readOnlyIndicatorConfig, data);

    const avatarCell = document.createElement('div');
    avatarCell.classList.add('w5-avatar-cell');
    if (reverse) avatarCell.classList.add('w5-avatar-cell--reverse');
    if (!params.value) {
        if (params.colDef.cellRendererParams.withEmptyPlaceholder) {
            const textContainer = document.createElement('div');
            textContainer.classList.add('w5-avatar-cell-text');
            textContainer.classList.add('w5-text-cell__placeholder');
            textContainer.innerText = params.t('label_placeholder_unamed_record');
            if (readOnlyIndicator) avatarCell.appendChild(readOnlyIndicator);
            avatarCell.appendChild(textContainer);
        }
        return avatarCell;
    }

    const text = value || '-';
    const src = data[otherFields?.src] || '';
    const placeholder = data[otherFields?.placeholder] || '';
    const alt = data[otherFields?.alt] || '';

    let svgContainer = null;
    let pngElement = null;
    const svg = (innerMethods?.getSvg && innerMethods.getSvg(data)) || null;
    const png = (innerMethods?.getPng && innerMethods.getPng(data.id)) || null;

    if (svg) {
        svgContainer = document.createElement('div');
        svgContainer.classList.add('w5-avatar-cell__icon');
        svgContainer.innerHTML = svg;
    }
    const textContainer = document.createElement('div');
    textContainer.classList.add('w5-avatar-cell-text');
    if ((!placeholder && !src && !innerMethods?.getAvatarSvg && !innerMethods?.getPng) || !text) {
        textContainer.innerText = text;
        if (readOnlyIndicator) avatarCell.appendChild(readOnlyIndicator);
        avatarCell.appendChild(textContainer);
        return avatarCell;
    }

    if (innerMethods?.getUrl && !params.onClick) {
        const linkElement = document.createElement('a');
        linkElement.href = innerMethods.getUrl(data);
        linkElement.innerText = text;
        textContainer.appendChild(linkElement);
    } else textContainer.innerText = text;

    let wrapperTextContainer = null;
    if (otherFields?.secondaryTextKey) {
        wrapperTextContainer = document.createElement('div');
        wrapperTextContainer.classList.add('w5-avatar-cell__text-wrapper');

        const secondaryTextContainer = document.createElement('div');
        secondaryTextContainer.classList.add('w5-avatar-cell-text--secondary');
        secondaryTextContainer.innerText = data[otherFields.secondaryTextKey] || '';

        wrapperTextContainer.appendChild(textContainer);
        wrapperTextContainer.appendChild(secondaryTextContainer);
    }

    const imgContainer = document.createElement('div');
    imgContainer.classList.add('w5-avatar-cell__img-container');
    const imgBox = document.createElement('div');
    imgBox.classList.add('w5-avatar-cell__img-box');

    if (png) {
        pngElement = document.createElement('img');
        pngElement.classList.add('w5-avatar-cell-png--avatar');
        imgBox.classList.add('w5-avatar-cell__img-box--png');
        if (otherParams.initials) {
            let name = otherParams.initials(data);
            let initials = getFirstTwoInitials(name);
            let initialsElement = document.createElement('span');
            imgBox.style.backgroundColor = colorHash.hex(initials);
            initialsElement.innerText = initials;
            initialsElement.classList.add('w5-avatar-cell__img-box--initials');
            imgBox.appendChild(initialsElement);
        }
        innerMethods.getPng(data.id).then(({ data: user }) => {
            if (user.photo) {
                imgBox.removeChild(imgBox.childNodes[0]);
                pngElement.src = `data:image/png;base64, ${user.photo}`;
            }
        });
    }
    if (isSquared) imgBox.classList.add('w5-avatar-cell__img-box--squared');
    if (onlyIcon) imgBox.classList.add('w5-avatar-cell__img-box--only-icon');
    if (imageSize) {
        // hoi-poi support this sizes: 'small', 'medium', 'large', 'big', 'huge'
        switch (imageSize) {
            case 'small':
                avatarCell.classList.add('w5-avatar-cell--small');
                imgBox.classList.add('w5-avatar-cell__img-box--small');
                break;
            case 'big':
                avatarCell.classList.add('w5-avatar-cell--big');
                imgBox.classList.add('w5-avatar-cell__img-box--big');
                break;
            case 'medium':
            default:
                avatarCell.classList.add('w5-avatar-cell--medium');
                imgBox.classList.add('w5-avatar-cell__img-box--medium');
                break;
        }
    } else {
        avatarCell.classList.add('w5-avatar-cell--medium');
        imgBox.classList.add('w5-avatar-cell__img-box--medium');
    }

    const appendNewImageParams = {
        imgContainer,
        pngElement,
        imgBox,
        svgContainer,
        avatarCell,
        textContainer: wrapperTextContainer || textContainer,
        readOnlyIndicator,
    };

    if (innerMethods?.getAvatarSvg) {
        const avatarSvg = innerMethods.getAvatarSvg(data);
        if (avatarSvg) {
            imgBox.innerHTML = avatarSvg;
            appendNewImage(appendNewImageParams);
        }
    } else if (src && srcHasPlaceholder[src]) {
        const img = new Image();
        img.src = existingPlaceholders?.[srcPlaceholders?.[src]];
        if (alt) img.alt = alt.charAt(0).toUpperCase();
        imgBox.appendChild(img);
        appendNewImage(appendNewImageParams);
    } else if (png && innerMethods.getPng) {
        imgBox.appendChild(pngElement);
        appendNewImage(appendNewImageParams);
    } else {
        const img = new Image();
        if (alt) img.alt = alt.charAt(0).toUpperCase();

        imgBox.appendChild(img);
        appendNewImage(appendNewImageParams);

        function setPlaceholder() {
            if (existingPlaceholders[placeholder]) {
                img.src = existingPlaceholders[placeholder];
                srcPlaceholders[src] = placeholder;
                srcHasPlaceholder[src] = true;
                return;
            }

            getDataUri(placeholder).then((dataUri) => {
                if (!dataUri) return;
                if (!existingPlaceholders[placeholder]) existingPlaceholders[placeholder] = dataUri;
                srcPlaceholders[src] = placeholder;
                srcHasPlaceholder[src] = true;
                img.src = dataUri;
            });
        }

        if (src) {
            img.src = existingPlaceholders[placeholder] || placeholder;
            getImageError(src)
                .then(() => {
                    // Avoiding calls to same placeholder
                    if (!existingPlaceholders[placeholder]) {
                        getDataUri(placeholder).then((dataUri) => {
                            existingPlaceholders[placeholder] = dataUri;
                        });
                    }
                    img.src = src;
                })
                .catch(setPlaceholder);
        } else {
            setPlaceholder();
        }
    }

    let tagElement = null;

    if (innerMethods?.getInfoForTag) {
        const tagInfo = innerMethods.getInfoForTag(data);
        tagElement = tagInfo && TagElement(tagInfo);
    }
    if (tagElement) {
        const wrapperElement = document.createElement('div');
        wrapperElement.classList.add('w5-avatar-cell__wrapper');
        wrapperElement.appendChild(avatarCell);
        wrapperElement.appendChild(tagElement);
        return wrapperElement;
    } else {
        return avatarCell;
    }
}

const appendNewImage = ({
    imgContainer,
    imgBox,
    svgContainer,
    avatarCell,
    textContainer,
    readOnlyIndicator,
}) => {
    imgContainer.appendChild(imgBox);
    if (svgContainer) imgContainer.appendChild(svgContainer);
    avatarCell.appendChild(imgContainer);
    if (readOnlyIndicator) avatarCell.appendChild(readOnlyIndicator);
    avatarCell.appendChild(textContainer);
};

const getImageError = (src) =>
    new Promise((resolve, reject) => {
        if (!src) return reject();
        requestAnimationFrame(() => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(src);
            img.onerror = reject;
            img.src = src;
        });
    });

function toDataUrl(url, callback) {
    var xhr = new XMLHttpRequest();
    xhr.onload = function () {
        var reader = new FileReader();
        reader.onloadend = function () {
            callback(reader.result);
        };
        reader.readAsDataURL(xhr.response);
    };
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.send();
}

const getDataUri = (src) => {
    return new Promise((resolve, reject) => {
        if (!src) return resolve();
        requestAnimationFrame(() => {
            toDataUrl(src, (dataUri) => {
                resolve(dataUri);
            });
        });
    });
};
